import { apiGet, apiPost } from './client';
import type {
  CursorPage,
  DepositDetail,
  DepositRow,
  ListSummary,
  ManualStatusUpdateResponse,
  TrxStatus,
} from './types';

export type ApiAuditEntry = {
  id: number;
  direction: 'inbound' | 'outbound';
  party: 'merchant' | 'payok' | 'self';
  method: string;
  url: string;
  requestHeaders: unknown;
  requestBody: unknown;
  responseStatus: number | null;
  responseHeaders: unknown;
  responseBody: unknown;
  durationMs: number | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
  /** true when reconstructed from legacy deposit-row / webhook_outbox fields. */
  synthesised?: boolean;
};

export type ApiAuditLog = {
  rows: ApiAuditEntry[];
  canSeeMerchant: boolean;
  canSeePayok: boolean;
};

export type DepositFilter = {
  trxId?: string;
  referenceId?: string;
  createdBy?: string;
  fromIp?: string;
  merchantId?: string[];
  trxStatus?: TrxStatus[];
  method?: string[];
  createdAtFrom?: string;
  createdAtTo?: string;
  paidAtFrom?: string;
  paidAtTo?: string;
  settleAtFrom?: string;
  settleAtTo?: string;
  amountMin?: number;
  amountMax?: number;
  feeMin?: number;
  feeMax?: number;
  merchantRevenueMin?: number;
  merchantRevenueMax?: number;
  cursor?: string;
  /** 1-based page — switches the endpoint to OFFSET mode with `total`. */
  page?: number;
  limit?: number;
};

function buildQuery(filter: DepositFilter): string {
  const p = new URLSearchParams();
  if (filter.trxId) p.set('trxId', filter.trxId);
  if (filter.referenceId) p.set('referenceId', filter.referenceId);
  if (filter.createdBy) p.set('createdBy', filter.createdBy);
  if (filter.fromIp) p.set('fromIp', filter.fromIp);
  filter.merchantId?.forEach((m) => p.append('merchantId', String(m)));
  filter.trxStatus?.forEach((s) => p.append('trxStatus', s));
  filter.method?.forEach((m) => p.append('method', m));
  if (filter.createdAtFrom) p.set('createdAtFrom', filter.createdAtFrom);
  if (filter.createdAtTo) p.set('createdAtTo', filter.createdAtTo);
  if (filter.paidAtFrom) p.set('paidAtFrom', filter.paidAtFrom);
  if (filter.paidAtTo) p.set('paidAtTo', filter.paidAtTo);
  if (filter.settleAtFrom) p.set('settleAtFrom', filter.settleAtFrom);
  if (filter.settleAtTo) p.set('settleAtTo', filter.settleAtTo);
  if (filter.amountMin != null) p.set('amountMin', String(filter.amountMin));
  if (filter.amountMax != null) p.set('amountMax', String(filter.amountMax));
  if (filter.feeMin != null) p.set('feeMin', String(filter.feeMin));
  if (filter.feeMax != null) p.set('feeMax', String(filter.feeMax));
  if (filter.merchantRevenueMin != null) p.set('merchantRevenueMin', String(filter.merchantRevenueMin));
  if (filter.merchantRevenueMax != null) p.set('merchantRevenueMax', String(filter.merchantRevenueMax));
  if (filter.cursor) p.set('cursor', filter.cursor);
  if (filter.page) p.set('page', String(filter.page));
  if (filter.limit) p.set('limit', String(filter.limit));
  return p.toString();
}

export const depositsApi = {
  page: (filter: DepositFilter) => {
    const qs = buildQuery(filter);
    return apiGet<CursorPage<DepositRow>>(`/api/deposits/page${qs ? `?${qs}` : ''}`);
  },
  /** Aggregates (totals / per status / per method) over the same filter as `page`. */
  summary: (filter: DepositFilter) => {
    const { cursor: _c, page: _p, limit: _l, ...rest } = filter;
    const qs = buildQuery(rest);
    return apiGet<ListSummary>(`/api/deposits/summary${qs ? `?${qs}` : ''}`);
  },
  detail: (trxId: string) => apiGet<DepositDetail>(`/api/deposits/${trxId}/detail`),
  apiLog: (trxId: string) => apiGet<ApiAuditLog>(`/api/deposits/${trxId}/api-log`),
  /**
   * Re-queue a `deposit.paid` (or `deposit.failed`) webhook delivery to the
   * merchant. Permission-gated server-side: external user needs
   * `webhook.delivery.retry` on own merchant; internal needs
   * `webhook.delivery.internal.retry`. Returns the new outbox row id.
   */
  resendWebhook: (trxId: string) =>
    apiPost<{
      ok: true;
      outboxId: string;
      deliveryId: string;
      targetUrl: string;
      event: string;
    }>(`/api/deposits/${trxId}/resend-webhook`),
  /**
   * Manual status override (internal staff with `deposits.status.update`).
   * Applies the matching wallet movement server-side; with `resendCallback`
   * a fresh deposit.paid / deposit.failed webhook is queued to the merchant.
   */
  updateStatus: (trxId: string, input: { status: TrxStatus; resendCallback: boolean }) =>
    apiPost<ManualStatusUpdateResponse>(`/api/deposits/${trxId}/status`, input),
};
